import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { Download, Truck } from "lucide-react";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { IconSquare, ViewIconButton } from "components/DatatablesActionIcons";
import { showError } from "toast/toast";
import TrackShipmentModal, { type TrackData } from "components/Shipements/TrackShipmentModal";
import {
  downloadEasyPostLabelPdf,
  fetchEasyPostLabelsDatatablesalesOrder,
  getTrackingByCode,
  type EasyPostLabelRecord,
  type TrackingApiResponse,
} from "components/Shipements/Shipmentsapicalls";
import "./SalesOrderDetailsShipmentsTable.css";

type LabelRow = {
  id: string;
  tracking: string;
  carrier: string;
  service: string;
  amount: string;
  createdAt: string;
  labelUrl: string;
};

const columnHelper = createColumnHelper<LabelRow>();

const SHIPPING_LABEL_ACRONYMS: Record<string, string> = {
  ups: "UPS",
  usps: "USPS",
  dhl: "DHL",
  fedex: "FedEx",
};

/** e.g. `ups_ground` → `UPS Ground`, `fedex_2_day` → `FedEx 2 Day` */
function formatShipmentServiceLabel(raw: string): string {
  const s = String(raw ?? "").trim();
  if (!s || s === "—") return "—";

  return s
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => {
      const key = part.toLowerCase();
      if (SHIPPING_LABEL_ACRONYMS[key]) return SHIPPING_LABEL_ACRONYMS[key];
      if (/^\d+$/.test(part)) return part;
      if (part.length <= 4 && part === part.toUpperCase()) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
}

function normalizeLabelOrderDatabaseId(record: EasyPostLabelRecord): string {
  const raw = (record as { order_database_id?: string | { $oid?: string } }).order_database_id;
  if (raw && typeof raw === "object" && "$oid" in raw) {
    return String(raw.$oid ?? "").trim();
  }
  return String(raw ?? "").trim();
}

function labelMatchesOrderFilters(
  record: EasyPostLabelRecord,
  orderDatabaseId: string,
  orderNumber: string
): boolean {
  if (orderDatabaseId) {
    const dbId = normalizeLabelOrderDatabaseId(record);
    if (dbId) return dbId === orderDatabaseId;
  }
  if (orderNumber) {
    const num = String(record.order_id ?? "").trim();
    if (num) return num === orderNumber;
  }
  return false;
}

function mapLabelRecord(d: EasyPostLabelRecord): LabelRow {
  const rawId = d._id as string | { $oid?: string } | undefined;
  const id =
    typeof rawId === "object" && rawId != null && "$oid" in rawId
      ? String(rawId.$oid || "")
      : String(rawId || "");
  return {
    id,
    tracking: d.tracking_code || "—",
    carrier: d.carrier || "—",
    service: formatShipmentServiceLabel(d.service || "—"),
    amount: d.postage_amount ? `$${d.postage_amount}` : "$0.00",
    createdAt: d.created_at ? new Date(d.created_at).toLocaleString() : "—",
    labelUrl: d.label_url || "",
  };
}

function formatTrackDate(d: string) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

type Props = {
  token: string;
  /** Mongo order document id for `filters.order_database_id`. */
  orderIdFilter: string;
  /** Display order number fallback when label rows only have `order_id`. */
  orderNumberFilter: string;
};

export function SalesOrderDetailsShipmentsTable({
  token,
  orderIdFilter,
  orderNumberFilter,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [activeLabel, setActiveLabel] = useState<LabelRow | null>(null);

  useEffect(() => {
    const filterOrderId = String(orderIdFilter || "").trim();
    const filterOrderNumber = String(orderNumberFilter || "").trim();
    if (!token || (!filterOrderId && !filterOrderNumber)) {
      setRows([]);
      return;
    }

    const filters: Record<string, string> = {};
    if (filterOrderId) filters.order_database_id = filterOrderId;
    // if (filterOrderNumber) filters.order_number = filterOrderNumber;

    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchEasyPostLabelsDatatablesalesOrder(token, {
          page: 1,
          limit: 100,
          search: "",
          filters,
          sort: { field: "created_at", order: "desc" },
        });
        const data: EasyPostLabelRecord[] = Array.isArray(res.data) ? res.data : [];
        const matched = data.filter((row) =>
          labelMatchesOrderFilters(row, filterOrderId, filterOrderNumber)
        );
        if (!mounted) return;
        setRows(matched.map(mapLabelRecord));
      } catch (err) {
        if (!mounted) return;
        setRows([]);
        showError(err instanceof Error ? err.message : "Failed to load shipment labels");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token, orderIdFilter, orderNumberFilter]);

  const loadTrackingFor = useCallback(
    async (row: LabelRow | null): Promise<TrackData> => {
      const tno = String(row?.tracking ?? "").trim();
      if (!tno || tno === "—") {
        return {
          estimatedDeliveryTitle: "—",
          estimatedDeliverySub: "—",
          events: [{ title: "No tracking number found.", isActive: true }],
        };
      }
      if (!token) {
        return {
          estimatedDeliveryTitle: "—",
          estimatedDeliverySub: "—",
          events: [{ title: "Not authenticated.", isActive: true }],
        };
      }
      try {
        const raw = (await getTrackingByCode(token, tno)) as TrackingApiResponse;
        const details = Array.isArray(raw?.tracking_details) ? raw.tracking_details : [];
        const estDate = raw?.est_delivery_date ?? null;
        return {
          estimatedDeliveryTitle: estDate ? formatTrackDate(estDate) || estDate : "—",
          estimatedDeliverySub: estDate ? String(estDate).slice(0, 10) : "—",
          events:
            details.length > 0
              ? details.map((e, i) => ({
                  title: String(e?.message ?? e?.status_detail ?? e?.status ?? "—"),
                  dateLabel: e?.datetime ? formatTrackDate(e.datetime) || e.datetime : "",
                  isActive: i === 0,
                }))
              : [
                  {
                    title: raw?.status_detail ?? raw?.status ?? "No tracking details yet.",
                    dateLabel: "",
                    isActive: true,
                  },
                ],
        };
      } catch (e) {
        return {
          estimatedDeliveryTitle: "—",
          estimatedDeliverySub: "—",
          events: [
            { title: e instanceof Error ? e.message : "Failed to load tracking.", isActive: true },
          ],
        };
      }
    },
    [token]
  );

  const handleTrackClick = useCallback((row: LabelRow) => {
    setActiveLabel(row);
    setTrackOpen(true);
  }, []);

  const handleDownload = useCallback(
    async (labelId: string) => {
      if (!labelId) {
        showError("Label id missing");
        return;
      }
      if (!token) {
        showError("Session expired. Please login again.");
        return;
      }
      setDownloadingId(labelId);
      try {
        const { blob, filename } = await downloadEasyPostLabelPdf(token, labelId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "label.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to download label");
      } finally {
        setDownloadingId(null);
      }
    },
    [token]
  );

  const handleView = useCallback(
    async (labelId: string) => {
      if (!labelId) {
        showError("Label id missing");
        return;
      }
      if (!token) {
        showError("Session expired. Please login again.");
        return;
      }
      setDownloadingId(labelId);
      try {
        const { blob } = await downloadEasyPostLabelPdf(token, labelId);
        const url = window.URL.createObjectURL(blob);
        const opened = window.open(url, "_blank", "noopener,noreferrer");
        if (!opened) {
          showError("Unable to open label. Please allow pop-ups and try again.");
          window.URL.revokeObjectURL(url);
        } else {
          window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to view label");
      } finally {
        setDownloadingId(null);
      }
    },
    [token]
  );

  const columns = useMemo(
    () => [
      // columnHelper.display({
      //   id: "serial",
      //   header: () => <div className="th-text text-center">S.No.</div>,
      //   cell: (info) => <div className="text-sm text-center">{info.row.index + 1}</div>,
      // }),
      columnHelper.accessor("tracking", {
        header: () => <div className="th-text text-center">Tracking Number</div>,
        cell: (info) => {
          const row = info.row.original;
          const t = info.getValue();
          const canTrack = t && t !== "—";
          return canTrack ? (
            <div className="d-flex justify-content-center text-center">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-sm S-No order-link-1"
                style={{ fontSize: "inherit" }}
                onClick={() => handleTrackClick(row)}
              >
                {t}
              </button>
            </div>
          ) : (
            <span className="text-muted d-flex justify-content-center text-center text-sm">{t}</span>
          );
        },
      }),
      columnHelper.accessor("carrier", {
        header: () => <div className="th-text text-center">Courier Provider</div>,
        cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
      }),
      columnHelper.accessor("service", {
        header: () => <div className="th-text text-center">Service</div>,
        cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
      }),
      // columnHelper.accessor("amount", {
      //   header: () => <div className="th-text text-center">Label Charges</div>,
      //   cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
      // }),
      // columnHelper.accessor("createdAt", {
      //   header: () => <div className="th-text text-center">Created On</div>,
      //   cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
      // }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text text-center">Actions</div>,
        cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <ViewIconButton
              as="button"
              type="button"
              className="icon-square"
              title="View label"
              ariaLabel="View label"
              // onClick={() => window.open(row.original.labelUrl, "_blank", "noopener,noreferrer")}
              // disabled={!row.original.labelUrl}
              onClick={() => void handleView(row.original.id)}
              disabled={!row.original.id || downloadingId === row.original.id}
            />
            <IconSquare
              as="button"
              type="button"
              className="icon-square"
              title="Download"
              ariaLabel="Download"
              onClick={() => void handleDownload(row.original.id)}
              disabled={!row.original.id || downloadingId === row.original.id}
            >
              <Download size={16} strokeWidth={2} aria-hidden="true" />
            </IconSquare>
          </div>
        ),
      }),
    ],
    [downloadingId, handleDownload, handleView, handleTrackClick]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const filterOrderId = String(orderIdFilter || "").trim();
  const filterOrderNumber = String(orderNumberFilter || "").trim();
  if (!filterOrderId && !filterOrderNumber) return null;
  if (loading || rows.length === 0) return null;

  return (
    <>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-2 w-100 sales-order-shipments-table">
        <div className="ms-3 py-3">
          <h6 className="mb-0 order-tittle d-flex align-items-center">
            <Truck size={20} className="me-2" style={{ color: "#00a63e" }} aria-hidden="true" />
            <span>Shipments</span>
          </h6>
        </div>
        <div className="card-body pt-0 pb-3 px-3">
          <div className="rdt-desktop">
            <div className="production-scroll">
              <table className="table production-table align-middle mb-0 table-1">
                <DesktopTableParts table={table} />
              </table>
            </div>
          </div>
          <div className="rdt-mobile">
            <MobileCardsOnly table={table} titleColId="tracking" actionColId="actions" />
          </div>
        </div>
      </div>

      <TrackShipmentModal
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        trackingNumber={String(activeLabel?.tracking ?? "")}
        carrierName={String(activeLabel?.carrier ?? "")}
        initialData={null}
        loadTracking={() => loadTrackingFor(activeLabel)}
      />
    </>
  );
}
